<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, useDefaultExport, useLookup, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import { useCostColumn, useCurrencyColumn, usePriceColumn, useUnitOfMeasureColumn, useVatTypeColumn, useServiceColumn } from '@/composables/data-grid-column'
import { useArchivedAtColumn, useArchivedColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'

import { usePortalStore } from '@/stores/portal.store'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TagCellEditTemplate from '@/components/shared/TagCellEditTemplate.vue'
import ListingItemEdit from '@/components/shared/ListingItemEdit.vue'

const gridId = 'project-details-grid-v2'

const props = defineProps({
  data: Object,
  showArchived: Boolean,
  code: { type: String, default: null },
})

const i18n = getI18n()
const { gridRef } = useGridRef()
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.listingItem', 2) })

const { getTagChips } = useChips()
const { archiveWarning, archiveButton, archiveAsync, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { await planningStore.archiveListingItemAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteListingItemAsync(id, config) },
  gridRef: gridRef,
})

const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const editors = {}

const listingId = props.data?.key
const localizedTags = portalStore.getLocalizedTags()
const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.listingItemsUrl, {
  filter: ['listing_id', '=', listingId],
  customParamsFn: () => { return props.showArchived ? { show_archived: true } : {} }
})

// dialog state for custom edit form
const editItem = ref(null)

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { 
        name: 'edit', 
        visible: (e) => !e.row?.data?.archived,  
        onClick: (e) => { editItem.value = { ...e.row.data } }, 
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      { 
        name: 'clone', 
        icon: 'copy', 
        visible: (e) => !e.row?.data?.archived, 
        onClick: async(e) => { 
          await planningStore.upsertListingItemAsync(
            { 
              ...e.row.data,
              name: `${e.row.data.name} (${i18n.t('common.copy', [e.row.data.name])})`, 
              id: undefined,
              currency_ref: undefined, 
              currency_ref_code: undefined, 
              unit_of_measure_ref: undefined, 
              vat_type_ref: undefined 
            })
          await gridRef.value.instance.refresh()
        }
      },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '8rem',
  },
  {
    dataField: 'code',
    caption: i18n.t('common.code'),
    width: '6rem',
    filterValue: props.code || undefined,
    selectedFilterOperation: props.code ? '=' : undefined,
  },
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
    width: '12rem',
    sortOrder: 'asc'
  },
  {
    dataField: 'description',
    caption: i18n.t('common.description'),
    width: '12rem',
  },
  {
    dataField: 'tags',
    caption: i18n.t('common.tag', 2),
    width: '12rem',
    lookup: useLookup({
      dataSource: localizedTags,
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    setCellValue: (rowData, value) => (rowData.tags = value),
    cellTemplate: 'tag-cell-template',
    editCellTemplate: 'tag-cell-edit-template',
    validationRules: [{ type: 'required' }]
  },
  useServiceColumn(),
  usePriceColumn(),
  useCostColumn(),
  useCurrencyColumn({ editors: editors, convertFields: ['price', 'cost'] }),
  useUnitOfMeasureColumn(),
  useVatTypeColumn(),
  useArchivedAtColumn(),
  useArchivedColumn(),
]

const gridOptions = useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: {
    mode: 'popup',
    allowUpdating: true,
    allowDeleting: true,
    useIcons: true,
  },
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onExporting: onExporting,
  export: exportOptions,
  onRowPrepared: onArchiveRowPrepared,
  onRowRemoving: onRowRemoving,
  allowAdding: !props.data?.data?.archived,
  addButton: { onClick: () => { editItem.value = { listing: listingId } } },
  isDetailGrid: true,
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}

</script>
<template>
  <div>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <q-dialog :model-value="editItem !== null" class="dialog-md" @hide="updateGrid">
      <listing-item-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :listing-id="listingId"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #tag-cell-template="{ data: templateOptions }">
        <chip-template :values="getTagChips(templateOptions.data.tags)" />
      </template>
      <template #tag-cell-edit-template="{ data: templateOptions }">
        <tag-cell-edit-template
          :value="templateOptions.data.tags"
          :data-source="localizedTags"
          :hide-selected-item="false"
          @value-changed="(e) => templateOptions.setValue(e.value)"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
